import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OrderingSelectors } from 'polygon-ordering';
import { RedcatApiHandler } from 'polygon-utils';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { setVerificationResendAt, clearVerificationResendAt } from '../slices/verificationResendAt';
import getThemeLookup from '../selectors/getThemeLookup';
import history from '../history';
import Text from '../components/Text';
import StandardButton from '../components/StandardButton';
import SendVerificationButton from '../components/SendVerificationButton';
import ScreenHero from '../components/ScreenHero';
import { DEVICE_TYPES } from '../constants';
import combineStyles from '../utils/combineStyles';
import { CONTAINER_PROPERTIES } from '../utils/theme';

const { getVerificationAttemptsConfig, getMember, getLoyaltyEnableSmsVerification } =
  OrderingSelectors;

const maskContact = (contact: string | undefined): string => {
  if (!contact) return '';
  const atIndex = contact.indexOf('@');
  if (atIndex > -1) {
    // email: show first 2 chars + *** + domain
    return contact.slice(0, 2) + '***' + contact.slice(atIndex);
  }
  // phone: show last 2 digits, mask the middle
  const digits = contact.replace(/\D/g, '');
  const last = digits.slice(-2);
  if (digits.startsWith('04')) {
    const prefix = '+61' + digits.slice(1, 3);
    return `${prefix} •• ••• •${last}`;
  }
  if (digits.startsWith('61')) {
    const prefix = '+61' + digits.slice(2, 4);
    return `${prefix} •• ••• •${last}`;
  }
  const prefix = digits.slice(0, 2);
  return `${prefix} •• ••• •${last}`;
};

// polygon-central sends `last_attempt` via Python's `datetime.isoformat()`, which emits
// microsecond precision (6 fractional digits). Chromium's Date parser accepts that
// leniently, but Safari/Firefox strictly enforce the ECMA-262 Date Time String Format
// (exactly 3 fractional digits) and return Invalid Date for anything longer.
const parseServerDate = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const normalised = value.replace(/(\.\d{3})\d+/, '$1');
  const time = new Date(normalised).getTime();
  return Number.isFinite(time) ? time : null;
};

const useResendCountDown = (
  verificationAttemptsConfig: {
    verificationMaxAttempts: number;
    verificationDelay: number;
    verificationLockout: number;
  },
  apiHandlerReady: boolean,
) => {
  const dispatch = useAppDispatch();
  const timerId = useRef<NodeJS.Timer | null>(null);
  const [verificationAttempts, setVerificationAttempts] = useState<
    { attempts: number; last_attempt: string; next_attempt?: string | number } | undefined
  >();
  const {
    verificationDelay = 30,
    verificationMaxAttempts,
    verificationLockout = 0,
  } = verificationAttemptsConfig;

  // Persisted absolute timestamp (epoch ms) at which resend becomes allowed again.
  // Storing an absolute time (rather than a decrementing counter in local state)
  const resendAtRaw = useAppSelector(state => state.verificationResendAt);
  // Sanity ceiling to catch genuinely corrupted values (e.g. NaN-derived from a bad
  // date parse in a previous session), NOT to tightly bound legitimate ones — the
  // reconciliation effect below sets this from the server's clock, which can be a few
  // seconds ahead of the client's, so this needs generous slack rather than an exact
  // match to verificationDelay/verificationLockout.
  const maxValidResendAt =
    Date.now() + (Math.max(verificationDelay, verificationLockout) + 300) * 1000;
  const resendAt =
    resendAtRaw != null && Number.isFinite(resendAtRaw) && resendAtRaw <= maxValidResendAt
      ? resendAtRaw
      : null;

  // Remaining seconds derived from the persisted timestamp. Recomputed on a ticking
  // clock so the label counts down and stays accurate even after the tab is backgrounded.
  const [now, setNow] = useState(() => Date.now());
  const rawDelay = resendAt ? Math.ceil((resendAt - now) / 1000) : 0;
  const delay = Number.isFinite(rawDelay) ? Math.max(0, rawDelay) : 0;
  const resendDisabled = delay > 0;

  const [showDisableLabel, setShowDisableLabel] = useState(false);
  const [isLoading, setLoading] = useState(true);

  // Wipe a corrupted persisted value from the store/localStorage so it doesn't keep
  // getting ignored in-memory on every render.
  useEffect(() => {
    if (resendAtRaw != null && resendAt == null) {
      dispatch(clearVerificationResendAt());
    }
  }, [resendAtRaw, resendAt]);

  // Begin a cooldown: persist the absolute unlock time so it outlives this component.
  // Refresh `now` immediately — it's otherwise only updated by the ticking interval below,
  // which doesn't start until resendDisabled flips true, so it can be stale by however long
  // the page sat idle before this click, making the very first render show a wrong delay.
  const startCooldown = (seconds: number = verificationDelay) => {
    const start = Date.now();
    setNow(start);
    dispatch(setVerificationResendAt(start + seconds * 1000));
  };

  useEffect(() => {
    if (apiHandlerReady) {
      RedcatApiHandler.authorisedFetch({
        method: 'GET',
        path: 'api/v1/profile/verification_attempts',
      })
        .then(
          (re: {
            data: { attempts: number; last_attempt: string; next_attempt?: string | number };
          }) => {
            setVerificationAttempts(re.data);
            const { attempts, last_attempt } = re.data;
            const lastAttemptAt = parseServerDate(last_attempt);
            const pastTime =
              lastAttemptAt != null ? Math.round((Date.now() - lastAttemptAt) / 1000) : null;
            if (attempts < 1 && pastTime !== null && pastTime > verificationDelay) {
              setShowDisableLabel(true);
            }
          },
        )
        .finally(() => setLoading(false));
    }
  }, [apiHandlerReady]);

  // Reconcile the persisted timestamp against the server's record of the last attempt.
  useEffect(() => {
    if (verificationAttempts) {
      const { attempts, last_attempt } = verificationAttempts;
      const lastAttemptAt = parseServerDate(last_attempt);
      const serverUnlockAt =
        lastAttemptAt != null ? lastAttemptAt + verificationDelay * 1000 : null;

      // Bound serverUnlockAt against the same sane ceiling as resendAt: `last_attempt` has
      // been observed coming back as a naive server-local timestamp (no UTC offset), which
      // parses many hours in the future and must not be trusted enough to overwrite a
      // legitimate, shorter-lived local cooldown.
      if (
        serverUnlockAt != null &&
        serverUnlockAt <= maxValidResendAt &&
        attempts > 0 &&
        serverUnlockAt > Date.now()
      ) {
        // Server still within the delay window — keep the button disabled until then.
        // Require more than a second's difference before overriding: `last_attempt` is
        // captured via a separate `new Date()` call a few ms after `startCooldown`'s
        // `Date.now()`, so without this margin that jitter alone rounds the very first
        // displayed delay up to 61s instead of 60.
        if (!resendAt || resendAt < serverUnlockAt - 1000) {
          dispatch(setVerificationResendAt(serverUnlockAt));
        }
      }
    }
  }, [verificationAttempts]);

  // Tick while a cooldown is active so `delay` recomputes each second.
  useEffect(() => {
    if (resendDisabled) {
      timerId.current = setInterval(() => setNow(Date.now()), 1000);
    }
    return () => {
      if (timerId.current) clearInterval(timerId.current);
    };
  }, [resendDisabled]);

  // Cooldown finished — clear the persisted timestamp and update the disabled label.
  // Guard on `verificationAttempts` being loaded: `delay` is 0 whenever no cooldown is
  useEffect(() => {
    if (delay < 1 && resendAt) dispatch(clearVerificationResendAt());
    if (delay < 1 && verificationAttempts && (verificationAttempts.attempts || 0) < 1) {
      setShowDisableLabel(true);
    }
  }, [delay, verificationAttempts]);

  return {
    verificationAttempts,
    setVerificationAttempts,
    resendDisabled,
    startCooldown,
    verificationDelay,
    verificationMaxAttempts,
    delay,
    showDisableLabel,
    setShowDisableLabel,
    isLoading,
  };
};

const RegistrationVerify: React.FC<{
  showContinueButton?: boolean;
  internalRedirect?: boolean;
}> = ({ showContinueButton, internalRedirect }) => {
  const deviceType = useAppSelector(state => state.deviceType);
  const isMobileDevice = deviceType === DEVICE_TYPES.MOBILE;

  const location = useLocation();
  const member = useAppSelector(getMember);

  const { state } = location;
  const loyaltyRequiresVerification = useAppSelector(
    state => state.config.loyaltyRequiresVerification,
  );
  const loyaltyEnableSmsVerification = useAppSelector(getLoyaltyEnableSmsVerification);
  const isVerified = !!member?.verified && loyaltyRequiresVerification;
  if (isVerified || (!state?.isRegistration && !member)) history.push('/');

  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const brandName = useAppSelector(state => state.config.appName);
  const apiHandlerReady = useAppSelector(state => state.apiHandlerReady);
  const verificationAttemptsConfig = useAppSelector(getVerificationAttemptsConfig);
  const method = loyaltyEnableSmsVerification
    ? t('verificationMethod.sms')
    : t('verificationMethod.email');

  const rawContact = loyaltyEnableSmsVerification ? member?.mobile : member?.email;
  const maskedContact = maskContact(rawContact);

  const { pathname } = location;

  // Full page (/registration_verify): "Thanks for joining" with masked contact
  // Compact inline (internalRedirect on private pages): per-page description + Resend Link button
  const getInlineDescription = (): string => {
    switch (pathname) {
      case '/profile':
        return t('RegistrationVerify.profileVerify', { method });
      case '/change-password':
        return t('RegistrationVerify.changePasswordVerify', { method });
      case '/delete-account':
        return t('RegistrationVerify.deleteAccountVerify', { method });
      case '/favourites':
        return t('RegistrationVerify.favouritesVerify', { method });
      case '/loyalty-history':
        return t('RegistrationVerify.loyaltyHistoryVerify', { method });
      case '/claim-missing-points':
        return t('RegistrationVerify.claimMissingVerify', { method });
      case '/offers':
        return t('RegistrationVerify.offersVerify', { contact: maskedContact });
      default:
        return t('RegistrationVerify.genericVerify', { method });
    }
  };

  const fullPageTitle = brandName
    ? t('RegistrationVerify.title', { name: brandName })
    : t('RegistrationVerify.titleNoName');
  const fullPageDescription = t('RegistrationVerify.welcome', { contact: maskedContact });
  const fullPageInstruction = t('RegistrationVerify.instruction');

  const {
    verificationAttempts,
    setVerificationAttempts,
    resendDisabled,
    startCooldown,
    verificationDelay,
    delay,
    showDisableLabel,
    isLoading,
  } = useResendCountDown(verificationAttemptsConfig, apiHandlerReady);

  const getResendEmailLabel = (): string => {
    if (!resendDisabled) {
      return t('RegistrationVerify.resendEmail', { method });
    }

    if (!showDisableLabel) {
      const minutes = Math.trunc(delay / 60);
      const seconds = (delay % 60).toLocaleString(undefined, {
        minimumIntegerDigits: 2,
      });

      return t('RegistrationVerify.resendEmailCountDown', {
        time: ` ${minutes}:${seconds}`,
        method,
      });
    }

    return t('RegistrationVerify.resendEmail', { method });
  };

  const resendEmailLabel = getResendEmailLabel();

  const handleResend = () => {
    startCooldown(verificationDelay);
    if ((verificationAttempts?.attempts || 0) > 0) {
      setVerificationAttempts({
        //@ts-ignore
        attempts: verificationAttempts?.attempts - 1,
        last_attempt: new Date().toISOString(),
      });
    }
  };

  const handleContinue = () => {
    history.push('/');
  };

  const renderResendButton = () => {
    if (isLoading) return null;

    if (showDisableLabel) {
      return (
        <Text
          style={{ padding: '0 0 20px', textAlign: 'center', opacity: '0.6' }}
          value={t('RegistrationVerify.unableResendEmail', { method })}
        />
      );
    }

    return (
      <SendVerificationButton
        onClick={handleResend}
        disabled={resendDisabled}
        label={resendEmailLabel}
        containerStyle={styles.buttonContainer}
      />
    );
  };

  if (isVerified) return null;

  // Compact inline layout — shown when replacing a private page (Profile, Favourites, etc.)
  if (internalRedirect) {
    return (
      <div style={styles.inlineWrapper}>
        <Text value={getInlineDescription()} style={styles.inlineDescription} />
        {renderResendButton()}
      </div>
    );
  }

  // Full page layout — shown on /registration_verify
  return (
    <div
      style={combineStyles([
        styles.wrapper,
        {
          paddingTop: 50,
          paddingBottom: 50,
          backgroundColor: isMobileDevice
            ? p('loyaltyMobileBackground', ['backgroundColor']).backgroundColor
            : p('loyaltyBackground', ['backgroundColor']).backgroundColor,
        },
        p('registrationVerifyContainer', CONTAINER_PROPERTIES),
      ])}
    >
      <div style={{ ...styles.container, padding: '50px 100px' }}>
        <div style={styles.top}>
          <Text value={fullPageTitle} style={{ fontWeight: 500, fontSize: 20, marginBottom: 30 }} />
          <Text value={fullPageDescription} style={{ textAlign: 'center', marginBottom: 16 }} />
          <Text value={fullPageInstruction} style={{ textAlign: 'center', marginBottom: 30 }} />
          {renderResendButton()}
        </div>
        {showContinueButton && (
          <div style={styles.bottom}>
            <StandardButton
              onClick={handleContinue}
              label={t('continue')}
              containerStyle={{ width: '70%' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const RegistrationVerifyScreen: React.FC<{
  showContinueButton?: boolean;
  internalRedirect?: boolean;
}> = ({ showContinueButton, internalRedirect }) => {
  return internalRedirect ? (
    <RegistrationVerify
      showContinueButton={showContinueButton}
      internalRedirect={internalRedirect}
    />
  ) : (
    <ScreenHero disablePSB={true}>
      <RegistrationVerify
        showContinueButton={showContinueButton}
        internalRedirect={internalRedirect}
      />
    </ScreenHero>
  );
};

const styles: Styles = {
  wrapper: {
    display: 'flex',
    justifyContent: 'center',
    flex: 1,
    height: '100vh',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: 660,
    backgroundColor: 'white',
  },
  top: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  bottom: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  inlineWrapper: {
    textAlign: 'center',
    width: '100%',
  },
  inlineDescription: {
    fontSize: 14,
    textAlign: 'center',
    width: '100%',
  },
  buttonContainer: {
    width: '70%',
    margin: 'auto',
    marginTop: 16,
  },
};
export const REGISTRATION_VERIFY = '/registration_verify';
export default RegistrationVerifyScreen;
